import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  FileText,
  Upload,
  Download,
  Plus,
  Loader2,
  DollarSign,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import { useContract, useDeleteContract } from '../services/contractService';
import { useLicenses } from '../services/licenseService';
import {
  useContractDocuments,
  useUploadDocument,
  useDeleteDocument,
  downloadDocument,
  formatFileSize,
} from '../services/documentService';
import {
  useCostRecords,
  useCostSummary,
  useCreateCostRecord,
  useUpdateCostRecord,
  useDeleteCostRecord,
} from '../services/costRecordService';
import {
  useContractDates,
  useCreateContractDate,
  useUpdateContractDate,
  useDeleteContractDate,
} from '../services/contractDateService';
import { formatCurrency, formatDate, formatCalendarDate } from '../utils/formatters';
import { calendarDay, calendarDayToIso, todayCalendarDay } from '../utils/calendarDate';
import {
  formatCostPerUser,
  formatSeatCount,
  isLicenseCostMismatch,
  describeLicenseCostMismatch,
} from '../utils/costPerUser';
import { paymentFrequencyLabel, contractDateTypeLabel, CONTRACT_DATE_TYPES } from '../types';
import type { LicenseListItem, DocumentListItem, CostRecord, CostRecordType, ContractDate } from '../types';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTable } from '@/components/ui/data-table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContractStatusBadge, ActiveStatusBadge, ExtractionStatusBadge } from '@/components/ui/status-badge';
import { useContractExtractions } from '../services/extractionService';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Detail item component - horizontal layout
 */
function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-3 border-b border-border">
      <span className="text-sm text-grey-500">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || '\u2014'}</span>
    </div>
  );
}

/**
 * "in N days" / "N days ago" / "today" for the notice deadline callout
 */
function describeDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return '';
  if (days === 0) return '(today)';
  if (days < 0) return `(${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} ago)`;
  return `(in ${days} ${days === 1 ? 'day' : 'days'})`;
}

/**
 * License utilization cell component
 */
function UtilizationCell({ allocated, total }: { allocated: number; total: number }) {
  const percentage = total > 0 ? (allocated / total) * 100 : 0;

  return (
    <div className="w-full pr-2">
      <div className="flex justify-between mb-1">
        <span className="text-xs text-grey-500">
          {allocated}/{total}
        </span>
        <span className="text-xs text-grey-500">{percentage.toFixed(0)}%</span>
      </div>
      <Progress
        value={Math.min(percentage, 100)}
        className="h-1"
        indicatorClassName={cn(
          percentage >= 90
            ? 'bg-destructive'
            : percentage >= 80
              ? 'bg-warning'
              : 'bg-success',
        )}
      />
    </div>
  );
}

/**
 * Human-readable message for a failed API call.
 *
 * The Axios interceptor in services/api.ts only forwards `data.message`, which the API
 * never sends. Validation failures arrive as ASP.NET ValidationProblemDetails
 * (`errors`: field -> string[]), and the controllers' 404/400 bodies as `{ error }`
 * or `{ title }`, so those are read here, in that order, before falling back to the
 * interceptor's `errorMessage` and the plain `message`.
 */
function describeApiError(err: unknown): string {
  const fallback = 'Something went wrong';
  if (!err || typeof err !== 'object') return fallback;

  const { response, errorMessage, message } = err as {
    response?: { data?: unknown };
    errorMessage?: unknown;
    message?: unknown;
  };
  const data = response?.data;

  if (data && typeof data === 'object') {
    const { errors, error, title } = data as { errors?: unknown; error?: unknown; title?: unknown };

    if (errors && typeof errors === 'object') {
      const messages = Object.values(errors as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string' && value.length > 0);
      if (messages.length > 0) return messages.join('; ');
    }
    if (typeof error === 'string' && error) return error;
    if (typeof title === 'string' && title) return title;
  }

  if (typeof errorMessage === 'string' && errorMessage) return errorMessage;
  if (typeof message === 'string' && message) return message;
  return fallback;
}

/**
 * Document category options
 */
const documentCategories = [
  'Contract',
  'Amendment',
  'Invoice',
  'SLA',
  'NDA',
  'Other',
];

/**
 * Contract Detail Page Component
 */
const ContractDetail: React.FC = () => {
  const navigate = useNavigate();
  const { canManageContracts, canManageLicenses, canManageDocuments, canManageCosts } = useAuth();
  const { id } = useParams({ strict: false }) as { id: string };

  // Contract data
  const { data: contract, isLoading, error } = useContract(id);
  const deleteMutation = useDeleteContract();

  // Licenses data with pagination
  const [licensePagination, setLicensePagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const { data: licensesData, isLoading: licensesLoading } = useLicenses({
    contractId: id,
    pageNumber: licensePagination.pageIndex + 1,
    pageSize: licensePagination.pageSize,
  });

  // Documents data
  const { data: documents, isLoading: documentsLoading, refetch: refetchDocuments } = useContractDocuments(id);
  const { data: extractions } = useContractExtractions(id);
  const pendingReviewCount = extractions?.pendingReviewCount ?? 0;
  const uploadMutation = useUploadDocument();
  const deleteDocMutation = useDeleteDocument();

  // Cost records data
  const { data: costRecords, isLoading: costRecordsLoading } = useCostRecords(id);
  const { data: costSummary } = useCostSummary(id);
  const createCostMutation = useCreateCostRecord();
  const updateCostMutation = useUpdateCostRecord();
  const deleteCostMutation = useDeleteCostRecord();

  // Milestones (contract dates) data
  const { data: contractDates, isLoading: contractDatesLoading } = useContractDates(id);
  const createDateMutation = useCreateContractDate();
  const updateDateMutation = useUpdateContractDate();
  const deleteDateMutation = useDeleteContractDate();

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = React.useState<string | null>(null);
  const [costDialogOpen, setCostDialogOpen] = React.useState(false);
  const [editingCostRecord, setEditingCostRecord] = React.useState<CostRecord | null>(null);
  const [deleteCostDialogOpen, setDeleteCostDialogOpen] = React.useState<string | null>(null);
  const [dateDialogOpen, setDateDialogOpen] = React.useState(false);
  const [editingContractDate, setEditingContractDate] = React.useState<ContractDate | null>(null);
  const [deleteDateDialogOpen, setDeleteDateDialogOpen] = React.useState<string | null>(null);
  const [dateDialogError, setDateDialogError] = React.useState<string | null>(null);

  // Milestone form state (date and reminder kept as the input strings; converted on save)
  const [dateForm, setDateForm] = React.useState({
    dateType: 'Renewal' as string,
    date: '',
    reminderDays: '',
    description: '',
  });

  // Cost record form state
  const [costForm, setCostForm] = React.useState({
    recordType: 'Payment' as CostRecordType,
    amount: '',
    currency: 'USD',
    date: todayCalendarDay(), // the user's day, not the UTC day (#65)
    description: '',
    invoiceNumber: '',
    costCenter: '',
  });

  // Upload form state
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = React.useState('Contract');
  const [uploadDescription, setUploadDescription] = React.useState('');

  const handleBack = () => navigate({ to: '/contracts' });
  const handleEdit = () => navigate({ to: `/contracts/${id}/edit` });
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(id);
      setDeleteDialogOpen(false);
      navigate({ to: '/contracts' });
    } catch (err) {
      console.error('Failed to delete contract:', err);
    }
  };

  const handleLicenseRowClick = (row: LicenseListItem) => {
    navigate({ to: `/licenses/${row.id}` });
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('contractId', id);
    formData.append('category', uploadCategory);
    if (uploadDescription) {
      formData.append('description', uploadDescription);
    }

    try {
      await uploadMutation.mutateAsync(formData);
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadCategory('Contract');
      setUploadDescription('');
      refetchDocuments();
    } catch (err) {
      console.error('Failed to upload document:', err);
    }
  };

  const handleDownload = async (doc: DocumentListItem) => {
    try {
      await downloadDocument(doc.id, doc.originalFileName);
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      await deleteDocMutation.mutateAsync(docId);
      setDeleteDocDialogOpen(null);
      refetchDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  const resetCostForm = () => {
    setCostForm({
      recordType: 'Payment',
      amount: '',
      currency: 'USD',
      date: todayCalendarDay(), // the user's day, not the UTC day (#65)
      description: '',
      invoiceNumber: '',
      costCenter: '',
    });
    setEditingCostRecord(null);
  };

  const handleOpenCostDialog = (record?: CostRecord) => {
    if (record) {
      setEditingCostRecord(record);
      setCostForm({
        recordType: record.recordType,
        amount: String(record.amount),
        currency: record.currency,
        date: calendarDay(record.date),
        description: record.description || '',
        invoiceNumber: record.invoiceNumber || '',
        costCenter: record.costCenter || '',
      });
    } else {
      resetCostForm();
    }
    setCostDialogOpen(true);
  };

  const handleSaveCostRecord = async () => {
    const payload = {
      recordType: costForm.recordType,
      amount: parseFloat(costForm.amount),
      currency: costForm.currency,
      date: calendarDayToIso(costForm.date),
      description: costForm.description || undefined,
      invoiceNumber: costForm.invoiceNumber || undefined,
      costCenter: costForm.costCenter || undefined,
    };

    try {
      if (editingCostRecord) {
        await updateCostMutation.mutateAsync({ contractId: id, id: editingCostRecord.id, data: payload });
      } else {
        await createCostMutation.mutateAsync({ contractId: id, data: payload });
      }
      setCostDialogOpen(false);
      resetCostForm();
    } catch (err) {
      console.error('Failed to save cost record:', err);
    }
  };

  const handleDeleteCostRecord = async (costId: string) => {
    try {
      await deleteCostMutation.mutateAsync({ contractId: id, id: costId });
      setDeleteCostDialogOpen(null);
    } catch (err) {
      console.error('Failed to delete cost record:', err);
    }
  };

  const costRecordTypes: CostRecordType[] = ['Payment', 'Invoice', 'Adjustment', 'Credit'];

  const resetDateForm = () => {
    setDateForm({ dateType: 'Renewal', date: '', reminderDays: '', description: '' });
    setEditingContractDate(null);
    setDateDialogError(null);
  };

  const handleOpenDateDialog = (milestone?: ContractDate) => {
    if (milestone) {
      setEditingContractDate(milestone);
      setDateForm({
        dateType: milestone.dateType,
        // Calendar day only, never through Date(): a UTC-midnight stamp viewed from a
        // western timezone would otherwise land on the previous day
        date: milestone.date.slice(0, 10),
        reminderDays: milestone.reminderDays === undefined ? '' : String(milestone.reminderDays),
        description: milestone.description ?? '',
      });
    } else {
      resetDateForm();
    }
    setDateDialogError(null);
    setDateDialogOpen(true);
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    resetDateForm();
  };

  const handleSaveContractDate = async () => {
    const payload = {
      dateType: dateForm.dateType,
      date: calendarDayToIso(dateForm.date),
      reminderDays: dateForm.reminderDays === '' ? undefined : Number(dateForm.reminderDays),
      description: dateForm.description || undefined,
    };

    setDateDialogError(null);
    try {
      if (editingContractDate) {
        await updateDateMutation.mutateAsync({ contractId: id, id: editingContractDate.id, data: payload });
      } else {
        await createDateMutation.mutateAsync({ contractId: id, data: payload });
      }
      closeDateDialog();
    } catch (err) {
      console.error('Failed to save milestone:', err);
      setDateDialogError(describeApiError(err));
    }
  };

  const handleDeleteContractDate = async (dateId: string) => {
    try {
      await deleteDateMutation.mutateAsync({ contractId: id, id: dateId });
      setDeleteDateDialogOpen(null);
    } catch (err) {
      console.error('Failed to delete milestone:', err);
    }
  };

  // The select lists the seven UI types; a stored value outside them (the API keeps
  // DateType free) is added as one extra option so the edit dialog can show it
  const dateTypeOptions: string[] =
    editingContractDate && !(CONTRACT_DATE_TYPES as readonly string[]).includes(editingContractDate.dateType)
      ? [...CONTRACT_DATE_TYPES, editingContractDate.dateType]
      : [...CONTRACT_DATE_TYPES];
  const dateMutationPending = createDateMutation.isPending || updateDateMutation.isPending;

  /**
   * License columns for DataTable
   */
  const licenseColumns: ColumnDef<LicenseListItem>[] = [
    {
      accessorKey: 'productName',
      header: 'Product',
      cell: ({ row }) => <span>{row.original.productName}</span>,
    },
    {
      accessorKey: 'licenseType',
      header: 'Type',
      enableSorting: false,
      cell: ({ row }) => <Badge variant="outline">{row.original.licenseType}</Badge>,
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total',
      cell: ({ row }) => <span className="text-center block">{row.original.totalQuantity}</span>,
    },
    {
      accessorKey: 'allocatedQuantity',
      header: 'Used',
      cell: ({ row }) => <span className="text-center block">{row.original.allocatedQuantity}</span>,
    },
    {
      accessorKey: 'availableQuantity',
      header: 'Free',
      cell: ({ row }) => (
        <span
          className={cn(
            'text-center block font-medium',
            row.original.availableQuantity > 0 ? 'text-success' : 'text-destructive',
          )}
        >
          {row.original.availableQuantity}
        </span>
      ),
    },
    {
      accessorKey: 'costPerUnit',
      header: 'Cost / Unit',
      enableSorting: false,
      cell: ({ row }) => {
        const license = row.original;
        if (license.costPerUnit <= 0) return <span className="block text-center">{'\u2014'}</span>;
        // The columns are declared before the loading guard, so the contract may still be missing here
        const annualValue = contract?.annualContractValue ?? 0;
        // Only an active license counts toward the seats, so only an active one gets the hint
        const mismatch = license.isActive && isLicenseCostMismatch(license, annualValue);
        const hint = mismatch ? describeLicenseCostMismatch(license, annualValue) : undefined;
        return (
          <span className="flex items-center justify-center gap-1">
            {formatCurrency(license.costPerUnit, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {hint && (
              // A data-quality hint, not an error: the figures differ by more than 10 percent
              <span className="inline-flex text-warning" title={hint} aria-label={hint} role="img">
                <AlertTriangle className="h-4 w-4" />
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'utilization',
      header: 'Utilization',
      enableSorting: false,
      cell: ({ row }) => (
        <UtilizationCell
          allocated={row.original.allocatedQuantity}
          total={row.original.totalQuantity}
        />
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !contract) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load contract details. Please try again later.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
            Back to Contracts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Contracts
        </Button>

        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{contract.title}</h1>
            <div className="flex items-center gap-2">
              <span className="text-grey-500">{contract.contractNumber}</span>
              <ContractStatusBadge status={contract.status} />
            </div>
          </div>

          {canManageContracts && (
            <div className="flex gap-2">
              <Button onClick={handleEdit}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={handleDeleteClick}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Notice deadline: the last day to give notice, computed by the API from the end date and notice period.
          Only a live contract can still act on it, so finished or archived ones get no callout. */}
      {contract.noticeDeadline && !contract.isArchived && contract.status !== 'Expired' && contract.status !== 'Terminated' && (
        <Card
          data-testid="notice-deadline"
          className={cn(
            'mb-6 border-l-4',
            (contract.daysUntilNoticeDeadline ?? 0) < 0
              ? 'border-l-destructive'
              : (contract.daysUntilNoticeDeadline ?? 0) <= 30
                ? 'border-l-warning'
                : 'border-l-primary',
          )}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <CalendarClock className="h-6 w-6 text-grey-500 shrink-0" />
            <div>
              <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Notice deadline</p>
              <p className="text-lg font-semibold">
                {formatCalendarDate(contract.noticeDeadline)}
                <span className="ml-2 text-sm font-normal text-grey-500">
                  {describeDays(contract.daysUntilNoticeDeadline)}
                </span>
              </p>
              <p className="text-sm text-grey-500">
                {contract.noticeRequiredDays} days before the end date ({formatCalendarDate(contract.endDate)})
                {contract.autoRenews && contract.renewalTermMonths
                  ? `; without notice the contract renews for ${contract.renewalTermMonths} months`
                  : ''}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Main Contract Information - Combined Card */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="grid grid-cols-12 gap-8">
                {/* Left Column - Basic Info & Dates */}
                <div className="col-span-12 md:col-span-6">
                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                    Contract Details
                  </p>
                  <DetailItem label="Vendor" value={contract.vendorName} />
                  <DetailItem label="Contract Type" value={contract.contractType} />
                  <DetailItem label="Status" value={contract.status} />
                  <DetailItem label="Owner" value={contract.ownerName} />
                  <DetailItem label="Department" value={contract.departmentName} />
                  <DetailItem label="Auto-Renews" value={contract.autoRenews ? 'Yes' : 'No'} />
                  <DetailItem
                    label="Notice Period"
                    value={contract.noticeRequiredDays > 0 ? `${contract.noticeRequiredDays} days` : 'None'}
                  />
                  <DetailItem
                    label="Renewal Term"
                    value={contract.renewalTermMonths ? `${contract.renewalTermMonths} months` : null}
                  />
                  <DetailItem
                    label="Parent Contract"
                    value={
                      contract.parentContractId ? (
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => navigate({ to: `/contracts/${contract.parentContractId}` })}
                        >
                          {contract.parentContractNumber ?? 'View parent'}
                        </button>
                      ) : null
                    }
                  />

                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mt-6 mb-4">
                    Important Dates
                  </p>
                  <DetailItem label="Start Date" value={formatCalendarDate(contract.startDate)} />
                  <DetailItem label="End Date" value={formatCalendarDate(contract.endDate)} />
                  {contract.renewalDate && (
                    <DetailItem label="Renewal Date" value={formatCalendarDate(contract.renewalDate)} />
                  )}
                </div>

                {/* Right Column - Financial Info & Notes */}
                <div className="col-span-12 md:col-span-6">
                  <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                    Financial Information
                  </p>
                  <DetailItem
                    label="Total Value"
                    value={formatCurrency(contract.totalContractValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  />
                  <DetailItem
                    label="Annual Value"
                    value={formatCurrency(contract.annualContractValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  />
                  <DetailItem
                    label="Seats"
                    value={<span data-testid="seat-count">{formatSeatCount(contract.seatCount)}</span>}
                  />
                  <DetailItem
                    label="Cost per User"
                    value={<span data-testid="cost-per-user">{formatCostPerUser(contract.costPerUser)}</span>}
                  />
                  <DetailItem label="Currency" value={contract.currency} />
                  <DetailItem label="Payment Frequency" value={paymentFrequencyLabel(contract.paymentFrequency)} />
                  {contract.paymentTerms && (
                    <DetailItem label="Payment Terms" value={contract.paymentTerms} />
                  )}

                  {(contract.description || contract.notes) && (
                    <>
                      <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mt-6 mb-4">
                        Additional Information
                      </p>
                      {contract.description && (
                        <div className="py-3">
                          <p className="text-sm text-grey-500 mb-1">Description</p>
                          <p className="text-sm">{contract.description}</p>
                        </div>
                      )}
                      {contract.notes && (
                        <div className="py-3">
                          <p className="text-sm text-grey-500 mb-1">Notes</p>
                          <p className="text-sm whitespace-pre-wrap">{contract.notes}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Metadata */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="h-full bg-grey-50">
            <CardContent className="p-6">
              <p className="text-xs font-medium text-grey-500 uppercase tracking-wider mb-4">
                Record Information
              </p>
              <DetailItem label="Created" value={formatDate(contract.createdAt, 'PPpp')} />
              <DetailItem label="Last Updated" value={formatDate(contract.updatedAt, 'PPpp')} />
              <DetailItem label="License Count" value={licensesData?.totalCount || contract.licenseCount || 0} />
              <DetailItem label="Document Count" value={documents?.length || contract.documentCount || 0} />
            </CardContent>
          </Card>
        </div>

        {/* Licenses Table */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Licenses ({licensesData?.totalCount || contract.licenseCount || 0})
                </h2>
                {canManageLicenses && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate({ to: '/licenses/new' })}
                  >
                    <Plus className="h-4 w-4" />
                    Add License
                  </Button>
                )}
              </div>
              <Separator className="mb-4" />

              <DataTable
                columns={licenseColumns}
                data={licensesData?.items || []}
                rowCount={licensesData?.totalCount || 0}
                isLoading={licensesLoading}
                pagination={licensePagination}
                onPaginationChange={setLicensePagination}
                onRowClick={handleLicenseRowClick}
                emptyMessage="No licenses associated with this contract"
                pageSizeOptions={[5, 10, 25]}
              />
            </CardContent>
          </Card>
        </div>

        {/* Milestones (contract dates) Table */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Milestones ({contractDates?.length || 0})
                </h2>
                {canManageContracts && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDateDialog()}
                  >
                    <CalendarClock className="h-4 w-4" />
                    Add Milestone
                  </Button>
                )}
              </div>
              <Separator className="mb-4" />

              {contractDatesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : contractDates && contractDates.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reminder</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractDates.map((milestone) => (
                      <TableRow
                        key={milestone.id}
                        className={cn('hover:bg-grey-50', milestone.daysUntil < 0 && 'text-grey-500')}
                      >
                        <TableCell>{formatCalendarDate(milestone.date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{contractDateTypeLabel(milestone.dateType)}</Badge>
                        </TableCell>
                        <TableCell className="max-w-64 truncate">{milestone.description || '—'}</TableCell>
                        <TableCell>
                          {milestone.reminderDays ? (
                            <span className="inline-flex items-center gap-2">
                              {milestone.reminderDays} days before
                              {milestone.isReminderSent && <Badge variant="outline">Sent</Badge>}
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canManageContracts && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Edit milestone"
                                    onClick={() => handleOpenDateDialog(milestone)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Delete milestone"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => setDeleteDateDialogOpen(milestone.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-grey-500">No milestones yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cost Records Table */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">
                    Cost Records ({costRecords?.length || 0})
                  </h2>
                  {costSummary && costSummary.recordCount > 0 && (
                    <p className="text-sm text-grey-500 mt-1">
                      Total: {formatCurrency(costSummary.totalAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
                {canManageCosts && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenCostDialog()}
                  >
                    <DollarSign className="h-4 w-4" />
                    Add Cost Record
                  </Button>
                )}
              </div>
              <Separator className="mb-4" />

              {costRecordsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : costRecords && costRecords.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Cost Center</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costRecords.map((record) => (
                      <TableRow key={record.id} className="hover:bg-grey-50">
                        <TableCell>{formatCalendarDate(record.date)}</TableCell>
                        <TableCell>
                          <Badge variant={record.recordType === 'Credit' ? 'default' : 'outline'}>
                            {record.recordType}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-48 truncate">{record.description || '\u2014'}</TableCell>
                        <TableCell>{record.invoiceNumber || '\u2014'}</TableCell>
                        <TableCell>{record.costCenter || '\u2014'}</TableCell>
                        <TableCell className={cn(
                          'text-right font-medium',
                          record.recordType === 'Credit' ? 'text-success' : '',
                        )}>
                          {record.recordType === 'Credit' ? '-' : ''}
                          {formatCurrency(record.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {canManageCosts && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Edit cost record"
                                    onClick={() => handleOpenCostDialog(record)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Delete cost record"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => setDeleteCostDialogOpen(record.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-grey-500">No cost records yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Documents Table */}
        <div className="col-span-12">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">
                    Documents ({documents?.length || contract.documentCount || 0})
                  </h2>
                  {pendingReviewCount > 0 && (
                    <Badge variant="warning" data-testid="documents-pending-review">
                      {pendingReviewCount} need{pendingReviewCount === 1 ? 's' : ''} review
                    </Badge>
                  )}
                </div>
                {canManageDocuments && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Document
                  </Button>
                )}
              </div>
              <Separator className="mb-4" />

              {documentsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : documents && documents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id} className="hover:bg-grey-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-grey-400" />
                            <span className="text-sm">{doc.originalFileName}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="outline">{doc.category}</Badge>
                            <ExtractionStatusBadge
                              status={doc.extractionStatus}
                              isExtractable={doc.isExtractable}
                              error={doc.extractionError}
                            />
                          </div>
                        </TableCell>
                        <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                        <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                        <TableCell>{doc.uploadedBy}</TableCell>
                        <TableCell className="text-right">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Download document"
                                onClick={() => handleDownload(doc)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Download</TooltipContent>
                          </Tooltip>
                          {canManageDocuments && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Delete document"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setDeleteDocDialogOpen(doc.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-grey-500">No documents uploaded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Contract Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contract</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contract? This action cannot be undone.
              All associated data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Button
                variant="outline"
                className="w-full py-6"
                onClick={() => document.getElementById('file-upload-input')?.click()}
              >
                <Upload className="h-4 w-4" />
                {uploadFile ? uploadFile.name : 'Choose File'}
              </Button>
              <input
                id="file-upload-input"
                type="file"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              />
              <p className="text-xs text-grey-500 mt-2">
                Allowed: PDF, Word, Excel, Images (max 50MB)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploadMutation.isPending}>
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <AlertDialog open={!!deleteDocDialogOpen} onOpenChange={(open) => { if (!open) setDeleteDocDialogOpen(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDocDialogOpen && handleDeleteDocument(deleteDocDialogOpen)}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deleteDocMutation.isPending}
            >
              {deleteDocMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Cost Record Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={(open) => { if (!open) { setCostDialogOpen(false); resetCostForm(); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCostRecord ? 'Edit Cost Record' : 'Add Cost Record'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Record Type *</Label>
                <Select value={costForm.recordType} onValueChange={(v) => setCostForm(f => ({ ...f, recordType: v as CostRecordType }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {costRecordTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={costForm.amount}
                  onChange={(e) => setCostForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={costForm.date}
                  onChange={(e) => setCostForm(f => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Input
                  value={costForm.currency}
                  maxLength={3}
                  onChange={(e) => setCostForm(f => ({ ...f, currency: e.target.value.toUpperCase() }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Invoice Number</Label>
                <Input
                  placeholder="e.g., INV-2026-001"
                  value={costForm.invoiceNumber}
                  onChange={(e) => setCostForm(f => ({ ...f, invoiceNumber: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Cost Center</Label>
                <Input
                  placeholder="e.g., IT-001"
                  value={costForm.costCenter}
                  onChange={(e) => setCostForm(f => ({ ...f, costCenter: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={costForm.description}
                onChange={(e) => setCostForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Payment description..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCostDialogOpen(false); resetCostForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveCostRecord}
              disabled={!costForm.amount || !costForm.date || createCostMutation.isPending || updateCostMutation.isPending}
            >
              {(createCostMutation.isPending || updateCostMutation.isPending) ? 'Saving...' : editingCostRecord ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Cost Record Confirmation Dialog */}
      <AlertDialog open={!!deleteCostDialogOpen} onOpenChange={(open) => { if (!open) setDeleteCostDialogOpen(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cost Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this cost record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCostDialogOpen && handleDeleteCostRecord(deleteCostDialogOpen)}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deleteCostMutation.isPending}
            >
              {deleteCostMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Milestone Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={(open) => { if (!open) closeDateDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContractDate ? 'Edit Milestone' : 'Add Milestone'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={dateForm.dateType} onValueChange={(v) => setDateForm(f => ({ ...f, dateType: v }))}>
                  <SelectTrigger aria-label="Milestone type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dateTypeOptions.map((t) => (
                      <SelectItem key={t} value={t}>{contractDateTypeLabel(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={dateForm.date}
                  onChange={(e) => setDateForm(f => ({ ...f, date: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Reminder (days before)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                step={1}
                placeholder="e.g., 30"
                value={dateForm.reminderDays}
                onChange={(e) => setDateForm(f => ({ ...f, reminderDays: e.target.value }))}
              />
              <p className="text-xs text-grey-500">Leave empty for no reminder</p>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={dateForm.description}
                maxLength={500}
                onChange={(e) => setDateForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="What happens on this date..."
              />
            </div>

            {dateDialogError && (
              <Alert variant="destructive">
                <AlertDescription>{dateDialogError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDateDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveContractDate}
              disabled={!dateForm.date || dateMutationPending}
            >
              {dateMutationPending ? 'Saving...' : editingContractDate ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Milestone Confirmation Dialog */}
      <AlertDialog open={!!deleteDateDialogOpen} onOpenChange={(open) => { if (!open) setDeleteDateDialogOpen(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Milestone</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this milestone? Any pending reminder for it is removed too. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDateDialogOpen && handleDeleteContractDate(deleteDateDialogOpen)}
              className="bg-destructive text-white hover:bg-destructive-dark"
              disabled={deleteDateMutation.isPending}
            >
              {deleteDateMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContractDetail;
